/**
 * Whole-body IK solver using unified damped least-squares.
 *
 * All targets (pins + drag) go into a single Jacobian system with equal
 * weight. The iterative DLS solve naturally finds poses where shared
 * joints (hips) rotate for the drag target while downstream joints (legs)
 * compensate to keep pinned bones in place.
 *
 * Handles tree-topology FK (not just linear chains) by topologically
 * sorting all bones parent-before-child and propagating rotations through
 * the full hierarchy.
 */

import * as THREE from "three/webgpu";
import { getRestPose } from "./boneRegistry.ts";
import { applyConstraint, getConstraint } from "./constraints.ts";
import {
  flushWholeBodyIteration,
  isIKLogEnabled,
  logWholeBodyDispatch,
  logWholeBodyIteration,
  logWholeBodyResult,
  logWholeBodyTargets,
} from "./ikDebugLog.ts";
import type { Quat4 } from "./quaternionMath.ts";

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

const DAMPING = 0.4;
const MAX_ITERATIONS = 25;
const TOLERANCE = 0.002;

/** Default joint mobility weights for bones not in any IK chain. */
const BONE_WEIGHTS: Record<string, number> = {
  root: 0.0, // never participates
  hips: 0.3,
  spine: 0.5,
  spine1: 0.55,
  spine2: 0.55,
  neck: 0.7,
  head: 0.5,
  shoulderL: 0.4,
  shoulderR: 0.4,
  upper_armL: 0.8,
  upper_armR: 0.8,
  forearmL: 0.8,
  forearmR: 0.8,
  handL: 0.6,
  handR: 0.6,
  thighL: 0.8,
  thighR: 0.8,
  shinL: 0.8,
  shinR: 0.8,
  footL: 0.5,
  footR: 0.5,
  toeL: 0.3,
  toeR: 0.3,
  // Twist bones are driven automatically — exclude from IK
  arm_twistL: 0.0,
  arm_twistR: 0.0,
  forearm_twistL: 0.0,
  forearm_twistR: 0.0,
  thigh_twistL: 0.0,
  thigh_twistR: 0.0,
  leg_twistL: 0.0,
  leg_twistR: 0.0,
};

const DEFAULT_WEIGHT = 0.6;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WholeBodyTarget {
  boneName: string;
  position: THREE.Vector3;
  weight: number; // pull strength
}

interface SkeletonState {
  names: string[]; // topological order (parent before child)
  parentIdx: Int32Array; // parentIdx[i] = index of parent, -1 for root
  localQuats: THREE.Quaternion[]; // mutable working copies
  offsets: THREE.Vector3[]; // bone-local offset from parent (constant)
  rootPos: THREE.Vector3;
  rootParentQuat: THREE.Quaternion;
}

interface DOFInfo {
  count: number;
  jointIdx: Int32Array;
  axisX: Float64Array;
  axisY: Float64Array;
  axisZ: Float64Array;
  weights: Float64Array;
}

// ---------------------------------------------------------------------------
// Scratch objects (avoid per-frame allocations)
// ---------------------------------------------------------------------------

const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _q1 = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _q3 = new THREE.Quaternion();

// Reusable FK arrays (grown as needed)
const _worldPos: THREE.Vector3[] = [];
const _worldQuat: THREE.Quaternion[] = [];

function ensureArrays(n: number) {
  while (_worldPos.length < n) _worldPos.push(new THREE.Vector3());
  while (_worldQuat.length < n) _worldQuat.push(new THREE.Quaternion());
}

// ---------------------------------------------------------------------------
// Skeleton state builder
// ---------------------------------------------------------------------------

/**
 * Build a solver-friendly representation of the skeleton from live bones.
 * Bones are topologically sorted (parent before child). Only "major" bones
 * that have constraints or known weights are included — finger bones and
 * other minor bones are excluded to keep the solve fast.
 */
export function buildSkeletonState(
  boneMap: Map<string, THREE.Bone>,
  currentPoses: ReadonlyMap<string, Quat4> | undefined,
  warmStartQuats: ReadonlyMap<string, Quat4> | undefined,
): SkeletonState {
  // Topological sort via BFS from root
  const root = boneMap.get("root");
  if (!root) throw new Error("Skeleton missing root bone");

  const ordered: THREE.Bone[] = [];
  const boneToIndex = new Map<THREE.Bone, number>();
  const queue: THREE.Bone[] = [root];

  while (queue.length > 0) {
    const bone = queue.shift()!;
    // Skip minor bones (fingers etc.) — they have names like "finger_indexL1"
    if (bone.name.startsWith("finger_")) continue;
    boneToIndex.set(bone, ordered.length);
    ordered.push(bone);
    for (const child of bone.children) {
      if ((child as THREE.Bone).isBone) queue.push(child as THREE.Bone);
    }
  }

  const n = ordered.length;
  const names: string[] = [];
  const parentIdx = new Int32Array(n);
  const localQuats: THREE.Quaternion[] = [];
  const offsets: THREE.Vector3[] = [];

  for (let i = 0; i < n; i++) {
    const bone = ordered[i];
    names.push(bone.name);

    // Parent index
    const parent = bone.parent;
    if (
      parent &&
      (parent as THREE.Bone).isBone &&
      boneToIndex.has(parent as THREE.Bone)
    ) {
      parentIdx[i] = boneToIndex.get(parent as THREE.Bone)!;
    } else {
      parentIdx[i] = -1;
    }

    // Local quaternion: warm-start → current pose → live bone
    const ws = warmStartQuats?.get(bone.name);
    const cp = currentPoses?.get(bone.name);
    if (ws) {
      localQuats.push(new THREE.Quaternion(ws[0], ws[1], ws[2], ws[3]));
    } else if (cp) {
      localQuats.push(new THREE.Quaternion(cp[0], cp[1], cp[2], cp[3]));
    } else {
      localQuats.push(bone.quaternion.clone());
    }

    // Bone offset in parent-local space (constant structural property)
    if (parentIdx[i] >= 0) {
      const parentBone = ordered[parentIdx[i]];
      _q1.identity();
      if (parentBone.parent) parentBone.parent.getWorldQuaternion(_q1);
      _q1.multiply(parentBone.quaternion); // parent world quat (using rest/bind)
      _q2.copy(_q1).invert();

      const parentPos = _v1;
      const bonePos = _v2;
      parentBone.getWorldPosition(parentPos);
      bone.getWorldPosition(bonePos);
      offsets.push(bonePos.clone().sub(parentPos).applyQuaternion(_q2));
    } else {
      offsets.push(new THREE.Vector3(0, 0, 0));
    }
  }

  // Root world position and parent quaternion
  const rootPos = new THREE.Vector3();
  root.getWorldPosition(rootPos);
  const rootParentQuat = new THREE.Quaternion();
  if (root.parent) root.parent.getWorldQuaternion(rootParentQuat);

  return { names, parentIdx, localQuats, offsets, rootPos, rootParentQuat };
}

// ---------------------------------------------------------------------------
// Forward kinematics (tree topology)
// ---------------------------------------------------------------------------

function forwardKinematics(state: SkeletonState): void {
  const n = state.names.length;
  ensureArrays(n);

  for (let i = 0; i < n; i++) {
    const pi = state.parentIdx[i];
    if (pi < 0) {
      // Root bone
      _worldQuat[i].copy(state.rootParentQuat).multiply(state.localQuats[i]);
      _worldPos[i].copy(state.rootPos);
    } else {
      _worldQuat[i].copy(_worldQuat[pi]).multiply(state.localQuats[i]);
      _worldPos[i]
        .copy(state.offsets[i])
        .applyQuaternion(_worldQuat[pi])
        .add(_worldPos[pi]);
    }
  }
}

// ---------------------------------------------------------------------------
// Dense matrix utilities
// ---------------------------------------------------------------------------

/**
 * In-place Cholesky decomposition of a symmetric positive-definite matrix A.
 * Overwrites the lower triangle of A with L such that A = L·Lᵀ.
 * Returns false if the matrix is not positive-definite.
 */
function choleskyDecompose(A: Float64Array, n: number): boolean {
  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = A[i * n + j];
      for (let k = 0; k < j; k++) {
        sum -= A[i * n + k] * A[j * n + k];
      }
      if (i === j) {
        if (sum <= 0) return false;
        A[i * n + j] = Math.sqrt(sum);
      } else {
        A[i * n + j] = sum / A[j * n + j];
      }
    }
  }
  return true;
}

/**
 * Solve L·Lᵀ·x = b where L is the lower-triangular Cholesky factor.
 * b is overwritten with the solution x.
 */
function choleskySolve(L: Float64Array, n: number, b: Float64Array): void {
  // Forward substitution: L·y = b
  for (let i = 0; i < n; i++) {
    let sum = b[i];
    for (let k = 0; k < i; k++) sum -= L[i * n + k] * b[k];
    b[i] = sum / L[i * n + i];
  }
  // Back substitution: Lᵀ·x = y
  for (let i = n - 1; i >= 0; i--) {
    let sum = b[i];
    for (let k = i + 1; k < n; k++) sum -= L[k * n + i] * b[k];
    b[i] = sum / L[i * n + i];
  }
}

// ---------------------------------------------------------------------------
// DOF enumeration
// ---------------------------------------------------------------------------

// Pre-allocated (grown once)
let _maxDOFs = 128;
let _dofJointIdx = new Int32Array(_maxDOFs);
let _dofAxisX = new Float64Array(_maxDOFs);
let _dofAxisY = new Float64Array(_maxDOFs);
let _dofAxisZ = new Float64Array(_maxDOFs);
let _dofWeights = new Float64Array(_maxDOFs);

function ensureDOFArrays(n: number) {
  if (n <= _maxDOFs) return;
  _maxDOFs = n * 2;
  _dofJointIdx = new Int32Array(_maxDOFs);
  _dofAxisX = new Float64Array(_maxDOFs);
  _dofAxisY = new Float64Array(_maxDOFs);
  _dofAxisZ = new Float64Array(_maxDOFs);
  _dofWeights = new Float64Array(_maxDOFs);
}

/**
 * Enumerate all DOFs for the skeleton. Each joint contributes 1 (hinge)
 * or 3 (ball/asymmetric_ball/unconstrained) DOFs.
 */
function enumerateDOFs(
  state: SkeletonState,
  lockedBones: ReadonlySet<string> | undefined,
): DOFInfo {
  let m = 0;
  const n = state.names.length;

  // Worst case: 3 DOFs per bone
  ensureDOFArrays(n * 3);

  for (let i = 0; i < n; i++) {
    const name = state.names[i];
    const w = BONE_WEIGHTS[name] ?? DEFAULT_WEIGHT;
    if (w < 1e-6) continue;
    if (lockedBones?.has(name)) continue;

    const constraint = getConstraint(name);

    if (constraint?.type === "hinge") {
      // Single DOF
      const ax = constraint.axis === "x" ? 1 : 0;
      const ay = constraint.axis === "y" ? 1 : 0;
      const az = constraint.axis === "z" ? 1 : 0;
      _dofJointIdx[m] = i;
      _dofAxisX[m] = ax;
      _dofAxisY[m] = ay;
      _dofAxisZ[m] = az;
      _dofWeights[m] = w;
      m++;
    } else {
      // 3 DOFs (ball, asymmetric_ball, or unconstrained)
      for (let d = 0; d < 3; d++) {
        _dofJointIdx[m] = i;
        _dofAxisX[m] = d === 0 ? 1 : 0;
        _dofAxisY[m] = d === 1 ? 1 : 0;
        _dofAxisZ[m] = d === 2 ? 1 : 0;
        _dofWeights[m] = w;
        m++;
      }
    }
  }

  return {
    count: m,
    jointIdx: _dofJointIdx,
    axisX: _dofAxisX,
    axisY: _dofAxisY,
    axisZ: _dofAxisZ,
    weights: _dofWeights,
  };
}

// ---------------------------------------------------------------------------
// Jacobian construction
// ---------------------------------------------------------------------------

/** Pre-allocated solver buffers, grown once and reused across frames. */
let _wbJ = new Float64Array(0);
let _wbError = new Float64Array(0);
let _wbA = new Float64Array(0);
let _wbV = new Float64Array(0);
let _wbDTheta = new Float64Array(0);

/**
 * Ensure solver buffers are large enough for the given dimensions.
 * Only reallocates when the current buffer is too small.
 */
function ensureSolverBuffers(rows: number, cols: number): void {
  const jSize = rows * cols;
  const aSize = rows * rows;
  if (_wbJ.length < jSize) _wbJ = new Float64Array(jSize);
  if (_wbError.length < rows) _wbError = new Float64Array(rows);
  if (_wbA.length < aSize) _wbA = new Float64Array(aSize);
  if (_wbV.length < rows) _wbV = new Float64Array(rows);
  if (_wbDTheta.length < cols) _wbDTheta = new Float64Array(cols);
}

/**
 * Build the Jacobian matrix J (targetDims × dofCount) for the given targets.
 * Each target contributes 3 rows (x, y, z of the position error).
 *
 * Target weight scales both the error and Jacobian rows, controlling how
 * strongly that target pulls relative to others in the unified solve.
 *
 * Writes into pre-allocated _wbJ and _wbError buffers (caller must call
 * ensureSolverBuffers first). Returns dimensions only.
 */
function buildJacobian(
  state: SkeletonState,
  targets: WholeBodyTarget[],
  dofs: DOFInfo,
  boneNameToIdx: Map<string, number>,
): { rows: number; cols: number } {
  const rows = targets.length * 3;
  const cols = dofs.count;

  // Zero the regions we'll write to
  _wbJ.fill(0, 0, rows * cols);
  _wbError.fill(0, 0, rows);

  for (let t = 0; t < targets.length; t++) {
    const target = targets[t];
    const effectorIdx = boneNameToIdx.get(target.boneName);
    if (effectorIdx === undefined) continue;

    const effectorPos = _worldPos[effectorIdx];
    const rowOff = t * 3;

    // Error vector
    _wbError[rowOff + 0] = (target.position.x - effectorPos.x) * target.weight;
    _wbError[rowOff + 1] = (target.position.y - effectorPos.y) * target.weight;
    _wbError[rowOff + 2] = (target.position.z - effectorPos.z) * target.weight;

    // For each DOF, compute Jacobian column only if the DOF's joint
    // is an ancestor of (or is) the effector bone.
    for (let d = 0; d < cols; d++) {
      const jointIdx = dofs.jointIdx[d];
      if (!isAncestorOrSelf(state, jointIdx, effectorIdx)) continue;

      // World-space rotation axis
      _v1
        .set(dofs.axisX[d], dofs.axisY[d], dofs.axisZ[d])
        .applyQuaternion(_worldQuat[jointIdx]);

      // J_col = axis × (effector - joint)
      _v2.subVectors(effectorPos, _worldPos[jointIdx]);
      const jx = _v1.y * _v2.z - _v1.z * _v2.y;
      const jy = _v1.z * _v2.x - _v1.x * _v2.z;
      const jz = _v1.x * _v2.y - _v1.y * _v2.x;

      _wbJ[(rowOff + 0) * cols + d] = jx * target.weight;
      _wbJ[(rowOff + 1) * cols + d] = jy * target.weight;
      _wbJ[(rowOff + 2) * cols + d] = jz * target.weight;
    }
  }

  return { rows, cols };
}

/**
 * Check if jointIdx is an ancestor of (or equal to) descendantIdx.
 */
function isAncestorOrSelf(
  state: SkeletonState,
  jointIdx: number,
  descendantIdx: number,
): boolean {
  let idx = descendantIdx;
  while (idx >= 0) {
    if (idx === jointIdx) return true;
    idx = state.parentIdx[idx];
  }
  return false;
}

// ---------------------------------------------------------------------------
// Apply angle deltas to local quaternions
// ---------------------------------------------------------------------------

function applyAngleDeltas(
  state: SkeletonState,
  dofs: DOFInfo,
  dTheta: Float64Array,
): void {
  for (let d = 0; d < dofs.count; d++) {
    const angle = dTheta[d];
    if (Math.abs(angle) < 1e-10) continue;

    const jointIdx = dofs.jointIdx[d];

    // World-space axis for this DOF
    _v1
      .set(dofs.axisX[d], dofs.axisY[d], dofs.axisZ[d])
      .applyQuaternion(_worldQuat[jointIdx]);

    // World-space rotation delta
    const halfAngle = angle * 0.5;
    const sinHalf = Math.sin(halfAngle);
    _q1.set(
      _v1.x * sinHalf,
      _v1.y * sinHalf,
      _v1.z * sinHalf,
      Math.cos(halfAngle),
    );

    // Convert to local space: localDelta = inv(parentWorld) × deltaWorld × parentWorld
    const pi = state.parentIdx[jointIdx];
    if (pi >= 0) {
      _q2.copy(_worldQuat[pi]).invert();
    } else {
      _q2.copy(state.rootParentQuat).invert();
    }
    _q3.copy(_q2).multiply(_q1);
    if (pi >= 0) {
      _q3.multiply(_worldQuat[pi]);
    } else {
      _q3.multiply(state.rootParentQuat);
    }

    // Apply: newLocal = localDelta × oldLocal
    state.localQuats[jointIdx].premultiply(_q3);
    state.localQuats[jointIdx].normalize();
  }
}

// ---------------------------------------------------------------------------
// Unified solver
// ---------------------------------------------------------------------------

/**
 * Solve whole-body IK with a unified damped least-squares system.
 *
 * All targets (pins + drag) enter one Jacobian with equal weight.
 * The iterative DLS solve finds poses where shared joints (hips) rotate
 * for the drag while downstream joints (legs) compensate to keep pins
 * planted. No priority weighting or phase splitting needed — the solver
 * naturally minimizes total error across all targets.
 */
export function solveWholeBodyIK(
  boneMap: Map<string, THREE.Bone>,
  targets: WholeBodyTarget[],
  objectId: string,
  currentPoses: ReadonlyMap<string, Quat4> | undefined,
  lockedBones: ReadonlySet<string> | undefined,
  constraintWeight: number,
  warmStartQuats: ReadonlyMap<string, Quat4> | undefined,
): Map<string, Quat4> {
  if (targets.length === 0) return new Map();

  const state = buildSkeletonState(boneMap, currentPoses, warmStartQuats);
  const n = state.names.length;

  const nameToIdx = new Map<string, number>();
  for (let i = 0; i < n; i++) nameToIdx.set(state.names[i], i);

  const dofs = enumerateDOFs(state, lockedBones);
  if (dofs.count === 0) return new Map();

  const logging = isIKLogEnabled();

  if (logging) {
    logWholeBodyDispatch(
      targets.map((t) => t.boneName),
      n,
      dofs.count,
    );
  }

  // Snapshot starting quats to measure joint deltas at the end
  const startQuats: THREE.Quaternion[] | undefined = logging
    ? state.localQuats.map((q) => q.clone())
    : undefined;

  const lambda2 = DAMPING * DAMPING;

  // Pre-allocate solver buffers once — reused across all iterations.
  // Dimensions are constant: rows = targets * 3, cols = dofs.count.
  const solverRows = targets.length * 3;
  const solverCols = dofs.count;
  ensureSolverBuffers(solverRows, solverCols);

  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    forwardKinematics(state);

    // Convergence: all targets within tolerance
    let maxError = 0;
    const targetErrors: { name: string; error: number }[] | undefined = logging
      ? []
      : undefined;
    for (const t of targets) {
      const idx = nameToIdx.get(t.boneName);
      if (idx === undefined) continue;
      const dx = t.position.x - _worldPos[idx].x;
      const dy = t.position.y - _worldPos[idx].y;
      const dz = t.position.z - _worldPos[idx].z;
      const err = Math.sqrt(dx * dx + dy * dy + dz * dz);
      maxError = Math.max(maxError, err);
      targetErrors?.push({ name: t.boneName, error: err });
    }
    const converged = maxError < TOLERANCE;
    if (logging) {
      logWholeBodyIteration(iter, targetErrors!, maxError, converged);
    }
    if (converged) break;

    // Build unified Jacobian for ALL targets (writes into _wbJ, _wbError)
    const { rows, cols } = buildJacobian(state, targets, dofs, nameToIdx);
    if (rows === 0) break;

    // A = J·W_dof·Jᵀ + λ²I (reuses _wbA)
    _wbA.fill(0, 0, rows * rows);
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j <= i; j++) {
        let sum = 0;
        for (let k = 0; k < cols; k++)
          sum += _wbJ[i * cols + k] * dofs.weights[k] * _wbJ[j * cols + k];
        if (i === j) sum += lambda2;
        _wbA[i * rows + j] = sum;
        _wbA[j * rows + i] = sum;
      }
    }

    // Solve A·v = error via Cholesky (reuses _wbV; copy error into v first)
    _wbV.set(_wbError.subarray(0, rows));
    if (!choleskyDecompose(_wbA, rows)) break;
    choleskySolve(_wbA, rows, _wbV);

    // δθ = W_dof · Jᵀ · v (reuses _wbDTheta)
    _wbDTheta.fill(0, 0, cols);
    for (let d = 0; d < cols; d++) {
      let jTv = 0;
      for (let r = 0; r < rows; r++) jTv += _wbJ[r * cols + d] * _wbV[r];
      _wbDTheta[d] = dofs.weights[d] * jTv;
    }

    applyAngleDeltas(state, dofs, _wbDTheta);
  }

  // Enforce joint constraints once after the solve — not per-iteration.
  // Per-iteration enforcement claws back the small DLS steps, causing the
  // solver to stall on large movements (0.03°/iter net progress).
  if (constraintWeight > 0) {
    for (let i = 0; i < n; i++) {
      const restQ = getRestPose(objectId, state.names[i]);
      state.localQuats[i] = applyConstraint(
        state.names[i],
        state.localQuats[i],
        restQ,
        constraintWeight,
      );
    }
  }

  // Log iteration summary, final target positions, and joint deltas
  if (logging) {
    flushWholeBodyIteration();

    // Final FK for target positions
    forwardKinematics(state);
    const targetInfo: {
      name: string;
      target: [number, number, number];
      actual: [number, number, number];
    }[] = [];
    for (const t of targets) {
      const idx = nameToIdx.get(t.boneName);
      if (idx === undefined) continue;
      targetInfo.push({
        name: t.boneName,
        target: [t.position.x, t.position.y, t.position.z],
        actual: [_worldPos[idx].x, _worldPos[idx].y, _worldPos[idx].z],
      });
    }
    logWholeBodyTargets(targetInfo);

    const jointDeltas: { name: string; angleDeg: number }[] = [];
    for (let i = 0; i < n; i++) {
      const dot = Math.min(
        1,
        Math.abs(
          startQuats![i].x * state.localQuats[i].x +
            startQuats![i].y * state.localQuats[i].y +
            startQuats![i].z * state.localQuats[i].z +
            startQuats![i].w * state.localQuats[i].w,
        ),
      );
      const angleDeg = 2 * Math.acos(dot) * (180 / Math.PI);
      jointDeltas.push({ name: state.names[i], angleDeg });
    }
    logWholeBodyResult(jointDeltas);
  }

  const result = new Map<string, Quat4>();
  for (let i = 0; i < n; i++) {
    const q = state.localQuats[i];
    result.set(state.names[i], [q.x, q.y, q.z, q.w]);
  }
  return result;
}
